import { useCallback, useEffect, useState } from "react";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { Plus, Trash2, FileSpreadsheet, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { Empty } from "@/components/Common";
import { WarehouseSelect } from "@/components/warehouse/StockTab";
import { ItemPicker } from "@/components/warehouse/ReceiptTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const MOVE_TYPES = { LOAD: "Carico", TRANSFER: "Trasferimento", UNLOAD: "Scarico", CONSUME: "Consumo intervento", RETURN: "Rientro dal campo",
  RETURN_RECEIVE: "Reso ricevuto", RETURN_RESTOCK: "Reso rimesso a stock", RETURN_REFURBISH: "Reso a ricondizionamento", RETURN_SCRAP: "Rottamazione", RETURN_SUPPLIER: "Reso a fornitore", ADJUST: "Rettifica inventario" };
const EMPTY = { type: "LOAD", from_warehouse_id: "", to_warehouse_id: "", notes: "" };
const LINE = () => ({ key: Math.random().toString(36).slice(2), item: null, qty: 1, serials: "", customer_id: "" });

const Field = ({ label, children }) => <div><Label className="micro-label">{label}</Label><div className="mt-1.5">{children}</div></div>;

const serialList = (s) => s.split(/[\s,;]+/).filter(Boolean);

const MoveLine = ({ l, i, items, needFrom, customers, update, remove, onSerial }) => {
  const auto = l.item?.serialized && needFrom && !!l.customer_id;
  const manualSerials = l.item?.serialized && !auto;
  return (
    <div className="border bg-muted/20 p-3" data-testid={`movement-line-${i}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {l.item
            ? <button type="button" className="flex h-11 w-full items-center border bg-white px-3 text-left text-sm" onClick={() => update({ item: null, serials: "", customer_id: "" })} title="Cambia articolo" data-testid={`movement-line-item-${i}`}><span className="truncate"><span className="font-mono text-xs font-semibold">{l.item.code}</span> · {l.item.name}</span></button>
            : <ItemPicker items={items} serials onPick={(it, s) => { if (s) { update({ item: it, serials: s.serial, customer_id: "" }); onSerial(s); } else update({ item: it }); }} testId={`movement-line-search-${i}`} placeholder="Cerca articolo o leggi il seriale dell'apparato…" />}
        </div>
        <Input className="h-11 w-24 bg-white text-right font-mono" type="number" min="0" step={l.item?.serialized ? "1" : "any"} disabled={manualSerials}
          value={manualSerials ? serialList(l.serials).length : l.qty} onChange={(e) => update({ qty: e.target.value })} aria-label="Quantità" data-testid={`movement-line-qty-${i}`} />
        <span className="flex h-11 w-8 items-center text-xs text-muted-foreground">{l.item?.unit || ""}</span>
        <Button type="button" variant="ghost" size="icon" className="h-11 w-11" onClick={remove} aria-label="Rimuovi riga" data-testid={`movement-line-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {l.item?.serialized && needFrom && (
        <Select value={l.customer_id || "manual"} onValueChange={(v) => update({ customer_id: v === "manual" ? "" : v })}>
          <SelectTrigger className="mt-2 bg-white" data-testid={`movement-line-customer-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="manual">Indico io i seriali</SelectItem><SelectItem value="none">Prelievo automatico · senza committente</SelectItem>
            {customers.map((p) => <SelectItem key={p.id} value={p.id}>Prelievo automatico · {p.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {manualSerials && <Textarea className="mt-2 min-h-20 bg-white font-mono" placeholder="Numeri di serie, uno per riga" value={l.serials} onChange={(e) => update({ serials: e.target.value })} data-testid={`movement-line-serials-${i}`} />}
    </div>
  );
};

export const openDdt = async (id, base = "/warehouse/ddt") => {
  const w = window.open("", "_blank");
  try {
    const r = await api.get(`${base}/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    if (w) w.location.href = url;
    else { const a = document.createElement("a"); a.href = url; a.download = "DDT.pdf"; a.click(); }
  } catch (e) {
    if (w) w.close();
    toast.error(errMsg(e));
  }
};

const MovementDialog = ({ open, onOpenChange, items, warehouses, onDone }) => {
  const { parties } = useData();
  const [f, setF] = useState(EMPTY);
  const [lines, setLines] = useState([LINE()]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const needFrom = f.type !== "LOAD";
  const needTo = f.type !== "UNLOAD";
  const customers = parties.filter((p) => p.roles.includes("customer"));
  const ready = lines.filter((l) => l.item);
  const updateLine = (key) => (patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const onSerial = (s) => {
    if (s.status !== "IN_STOCK" || !s.warehouse_id) return toast.warning(`S/N ${s.serial} non è a magazzino (${s.status})`);
    setF((x) => ({ ...x, type: x.type === "LOAD" ? "TRANSFER" : x.type, from_warehouse_id: x.from_warehouse_id || s.warehouse_id }));
  };
  const fromFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/warehouse/movements/parse-file", fd);
      const loaded = data.lines.map((x) => ({ ...LINE(), item: items.find((it) => it.id === x.item_id) || x, qty: x.qty, serials: x.serials.join("\n") }));
      if (loaded.length) setLines((ls) => [...ls.filter((l) => l.item), ...loaded]);
      toast.success(`${loaded.length} articoli caricati dal file`);
      if (data.unknown.length) toast.error(`Righe non riconosciute: ${data.unknown.map((u) => `riga ${u.row} (${u.code}${u.reason ? `, ${u.reason}` : ""})`).join(", ")}`, { duration: 12000 });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const save = async (withDdt) => {
    setBusy(true);
    const failed = [];
    const created = [];
    for (const l of ready) {
      const auto = l.item.serialized && needFrom && !!l.customer_id;
      try {
        const { data } = await api.post("/warehouse/movements", { type: f.type, notes: f.notes, item_id: l.item.id, qty: Number(l.qty) || 0,
          serials: auto || !l.item.serialized ? [] : serialList(l.serials), customer_id: auto ? l.customer_id : null, pick_qty: auto ? Number(l.qty) || 0 : 0,
          from_warehouse_id: needFrom ? f.from_warehouse_id : null, to_warehouse_id: needTo ? f.to_warehouse_id : null });
        created.push(data.id);
      } catch (e) {
        failed.push({ ...l, error: errMsg(e) });
      }
    }
    if (withDdt && created.length) {
      try {
        const { data } = await api.post("/warehouse/ddt", { movement_ids: created, notes: f.notes });
        toast.success(`DDT n. ${data.number} emesso`);
        await openDdt(data.id);
      } catch (e) {
        toast.error(`DDT non emesso: ${errMsg(e)}`);
      }
    }
    setBusy(false);
    if (ready.length > failed.length) toast.success(`${ready.length - failed.length} movimenti registrati`);
    failed.forEach((l) => toast.error(`${l.item.code}: ${l.error}`));
    if (!failed.length) {
      setF(EMPTY);
      setLines([LINE()]);
      onOpenChange(false);
    } else {
      setLines(failed.map(({ error, ...l }) => l));
    }
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="movement-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo movimento</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <Field label="Tipo">
            <Select value={f.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="LOAD">Carico (da fornitore)</SelectItem><SelectItem value="TRANSFER">Trasferimento (es. magazzino → furgone)</SelectItem><SelectItem value="UNLOAD">Scarico (reso, guasto, rettifica)</SelectItem></SelectContent>
            </Select>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            {needFrom && <Field label="Da magazzino"><WarehouseSelect value={f.from_warehouse_id} onChange={set("from_warehouse_id")} warehouses={warehouses} testId="movement-from-select" className="w-full" /></Field>}
            {needTo && <Field label="A magazzino"><WarehouseSelect value={f.to_warehouse_id} onChange={set("to_warehouse_id")} warehouses={warehouses} testId="movement-to-select" className="w-full" /></Field>}
          </div>
          <Field label={`Articoli e quantità (${ready.length})`}>
            <div className="grid gap-2">
              {lines.map((l, i) => <MoveLine key={l.key} l={l} i={i} items={items.filter((it) => it.active)} needFrom={needFrom} customers={customers} update={updateLine(l.key)} onSerial={onSerial}
                remove={() => setLines((ls) => (ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [LINE()]))} />)}
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" onClick={() => setLines((ls) => [...ls, LINE()])} data-testid="movement-add-line"><Plus className="mr-1 h-4 w-4" />Aggiungi articolo</Button>
                <Button type="button" variant="outline" asChild><label className="cursor-pointer" data-testid="movement-file-button"><FileSpreadsheet className="mr-1 h-4 w-4" />Carica da Excel
                  <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { fromFile(e.target.files[0]); e.target.value = ""; }} data-testid="movement-file-input" /></label></Button>
                <span className="text-xs text-muted-foreground">Colonne: Codice, Quantità e, per gli apparati, Seriale</span>
              </div>
            </div>
          </Field>
          <Field label="Note"><Input value={f.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="movement-notes" /></Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant={f.type === "TRANSFER" ? "outline" : "default"} onClick={() => save(false)} disabled={!ready.length || busy} data-testid="movement-save-button">{busy ? "Registrazione…" : `Registra ${ready.length > 1 ? `${ready.length} movimenti` : ""}`}</Button>
          {f.type === "TRANSFER" && <Button onClick={() => save(true)} disabled={!ready.length || busy} data-testid="movement-save-ddt-button"><FileText className="mr-1 h-4 w-4" />Registra e stampa DDT</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MovementsTab = ({ items, warehouses, onChanged }) => {
  const { can } = useAuth();
  const [wh, setWh] = useState("");
  const { companyId } = useData();
  const [raw, setRows] = useState([]);
  const ids = new Set(warehouses.map((w) => w.id));
  const rows = companyId ? raw.filter((m) => ids.has(m.from_warehouse_id) || ids.has(m.to_warehouse_id)) : raw;
  useEffect(() => { setWh(""); }, [companyId]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/warehouse/movements", { params: { warehouse_id: wh || undefined } }).then((r) => setRows(r.data)).catch(() => {}), [wh]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (m) => {
    try {
      await api.post(`/warehouse/movements/${m.id}/resolve`);
      toast.success("Anomalia verificata");
      load();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="movements-tab">
      <div className="mb-3 flex flex-wrap gap-3">
        <WarehouseSelect value={wh} onChange={setWh} warehouses={warehouses} all="Tutti i magazzini" testId="movements-warehouse-filter" />
        {can("inventory:write") && <Button className="ml-auto" onClick={() => setOpen(true)} data-testid="movement-new-button"><Plus className="mr-1 h-4 w-4" />Nuovo movimento</Button>}
      </div>
      {rows.length === 0 ? <Empty text="Nessun movimento" testId="movements-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Articolo</TableHead><TableHead className="text-right">Q.tà</TableHead><TableHead>Da → A</TableHead><TableHead>Rif. / note</TableHead><TableHead>Utente</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id} className={m.negative || m.anomaly ? "bg-red-50/60" : ""} data-testid={`movement-row-${m.id}`}>
                  <TableCell className="whitespace-nowrap font-mono text-xs">{fmtDateTime(m.at)}</TableCell>
                  <TableCell className="text-sm font-semibold">{MOVE_TYPES[m.type]}</TableCell>
                  <TableCell className="text-sm"><span className="font-mono text-xs">{m.item_code}</span> {m.item_name}{m.serials?.length > 0 && <div className="font-mono text-[11px] text-muted-foreground">S/N {m.serials.slice(0, 3).join(", ")}{m.serials.length > 3 && ` +${m.serials.length - 3}`}</div>}</TableCell>
                  <TableCell className="text-right font-mono">{m.qty}</TableCell>
                  <TableCell className="text-xs">{m.from_name || "Fornitore"} → {m.to_name || (m.type === "CONSUME" ? "Cliente" : "Uscita")}</TableCell>
                  <TableCell className="text-xs">
                    {m.ddt_id && <button className="mr-1 inline-flex items-center gap-1 font-mono font-semibold underline" onClick={() => openDdt(m.ddt_id)} data-testid={`movement-ddt-${m.id}`}><FileText className="h-3 w-3" />DDT {m.ddt_number}</button>}
                    {m.work_order_id && <Link className="font-mono font-semibold underline" to={`/ordini/${m.work_order_id}`}>{m.external_order_id}</Link>} {m.notes}
                    {m.warnings?.length > 0 && <div className="text-amber-700">{m.warnings.join(" · ")}</div>}
                    {m.anomaly && can("inventory:write") && <button className="mt-1 font-semibold text-red-700 underline" onClick={() => resolve(m)} data-testid={`movement-resolve-${m.id}`}>Segna come verificata</button>}
                    {m.resolved_by && <div className="text-emerald-700">Verificata da {m.resolved_by}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{m.by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <MovementDialog open={open} onOpenChange={setOpen} items={items} warehouses={warehouses} onDone={() => { load(); onChanged(); }} />
    </div>
  );
};
